import React, { memo, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import { useIntl } from '@atlassian/jira-intl';
import { useHideNavigationItem } from '@atlassian/jira-horizontal-nav-hide-item-service/src/services/useHideNavigationItem.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { useTabContext } from '@atlassian/navigation-kit-ui-tab';
import { fg } from '@atlassian/jira-feature-gating';
import type { HideItem_container$key } from './__generated__/HideItem_container.graphql';
import type { HideItem_tab$key } from './__generated__/HideItem_tab.graphql';
import messages from './messages.tsx';

type Props = {
	onClick: () => void;
	tabFragment: HideItem_tab$key;
	containerFragment: HideItem_container$key;
};

export const HideItem = memo(({ tabFragment, containerFragment, onClick }: Props) => {
	const { formatMessage } = useIntl();
	const hideNavigationItem = useHideNavigationItem();
	const { push } = useRouterActions();
	const { isSelected } = useTabContext();

	const tabData = useFragment(
		graphql`
			fragment HideItem_tab on JiraNavigationItem {
				id @required(action: THROW)
				__id @required(action: THROW)
			}
		`,
		tabFragment,
	);
	const containerData = useFragment(
		graphql`
			fragment HideItem_container on JiraContainerNavigationResult {
				... on JiraContainerNavigation {
					id @required(action: THROW)
					navigationItems(first: $first) {
						edges {
							node @required(action: THROW) {
								isDefault @required(action: THROW)
								url
							}
						}
					}
				}
			}
		`,
		containerFragment,
	);

	const handleClick = useCallback(() => {
		onClick();

		if (!containerData.id) {
			return;
		}

		const defaultTabUrl = containerData.navigationItems?.edges?.find((edge) => edge?.node.isDefault)
			?.node.url;

		hideNavigationItem({
			id: tabData.id,
			relayId: tabData.__id,
			containerNavigationId: containerData.id,
			onStart: () => {
				if (defaultTabUrl && isSelected && fg('jira_list_nin_merge_technical_changes')) {
					push(defaultTabUrl);
				}
			},
			// TODO Remove when cleaning up jira_list_nin_merge_technical_changes
			onCompleted: () => {
				if (defaultTabUrl && isSelected && !fg('jira_list_nin_merge_technical_changes')) {
					push(defaultTabUrl);
				}
			},
		});
	}, [containerData, hideNavigationItem, isSelected, onClick, push, tabData.__id, tabData.id]);

	return <DropdownItem onClick={handleClick}>{formatMessage(messages.hideItem)}</DropdownItem>;
});
